import { Badge, Dialog, DialogContent } from '@mui/material';
import {
  AdvancedSearchComponentType,
  IconLabelButton,
  IframelyMedia,
  InfiniteListMode,
  isDailyMotionVideo,
  mediaFromIframely,
  OneMedia,
  SearchParams,
  useDebounce,
} from '@prismamedia/one-components';
import React, { FC, useCallback, useEffect, useReducer, useState } from 'react';
import type { IndexRange } from 'react-virtualized';
import { DefaultMediaContent } from './Content';
import { MultimediaList } from './MultimediaList';
import { MultimediaTabs } from './MultimediaTabs';
import { MultimediaToolbar } from './MultimediaToolbar';
import { actions, initialState, reducer } from './reducer';
import { useStyles } from './styles';
import {
  ArticleFormat,
  ItemSerialized,
  MEDIA_TYPES_LABEL,
  MultimediaSearchPluginProps,
  ViewMode,
} from './types';

const PAGE_RESULT = 10;
const DEFAULT_PAGE = 1;
const DEFAULT_PAGE_RESULT = 25;

interface MultimediaPluginProps<Item> extends MultimediaSearchPluginProps<any> {
  addMedia?: (media: OneMedia | OneMedia[]) => void;
  customStyles?: React.CSSProperties;
  customToolbar?: React.ReactNode;
  defaultActions?: boolean;
  defaultText?: string;
  onClick?: (item: Item, items: Item[], medias: IframelyMedia[]) => void;
  onClose?: () => void;
  onSave?: (items: Item[], medias: IframelyMedia[]) => void;
  setManagerLoading?: (loading: boolean) => void;
  updateMedia?: (mediaToUpdate: OneMedia, idx: number) => void;
  advancedSearch?: AdvancedSearchComponentType;
  initialTypeSelected?: MEDIA_TYPES_LABEL | string;
}

const getArticleFormat = (mediaType: MEDIA_TYPES_LABEL) => {
  switch (mediaType) {
    case MEDIA_TYPES_LABEL.ARTICLES:
      return ArticleFormat.Rich;
    case MEDIA_TYPES_LABEL.VIDEOS:
      return ArticleFormat.Video;
    case MEDIA_TYPES_LABEL.SLIDESHOWS:
      return ArticleFormat.Slideshow;
    default:
      return null;
  }
};

export const MultimediaPlugin: FC<MultimediaPluginProps<ItemSerialized>> = ({
  addMedia,
  allowMultiSelect = true,
  allowSearch = true,
  allowedMedias,
  customStyles = { display: 'inline-block' },
  customToolbar,
  defaultActions = true,
  defaultText,
  itemToIframely,
  itemToRender,
  onClick,
  onClose,
  onSave,
  placeholder,
  searchFn,
  setManagerLoading,
  updateMedia,
  viewMode,
  suggestArticlesFn,
  advancedSearch,
  itemClickCallback,
  initialTypeSelected,
}) => {
  const [state, dispatch] = useReducer(reducer, initialState);
  const classes = useStyles({ allowSearch });
  const [searchParams, setSearchParams] = useState<SearchParams>({
    search: '',
  });
  const debouncedSearch = useDebounce<string>(searchParams.search || '', 500);

  const allowedSuggestedMedias = [
    MEDIA_TYPES_LABEL.ARTICLES,
    MEDIA_TYPES_LABEL.SLIDESHOWS,
    MEDIA_TYPES_LABEL.VIDEOS,
  ];

  const fetchItems = async (opts?: IndexRange) => {
    const { items, pluginType } = state;

    dispatch(actions.setLoading(true));

    if (!opts) {
      dispatch(actions.setItems([]));
    }

    const page =
      (searchParams.search === state.value && opts?.stopIndex) || DEFAULT_PAGE;
    const first = DEFAULT_PAGE_RESULT * (searchParams.search ? 2 : 1);
    const skip = opts
      ? opts.startIndex - 1
      : page === DEFAULT_PAGE
      ? (page - 1) * PAGE_RESULT
      : page;

    searchFn(searchParams, { first, skip }, pluginType)
      .then((res) => {
        let finalResult = [];

        finalResult =
          pluginType === MEDIA_TYPES_LABEL.VIDEOS
            ? res.filter((r) => {
                try {
                  const videos =
                    typeof r.medias === 'string'
                      ? JSON.parse(r.medias)
                      : r.medias;
                  return videos.some((media: OneMedia) => {
                    return isDailyMotionVideo(media);
                  });
                } catch (error) {
                  console.error(error);
                }
              })
            : res;

        const newItems = opts ? [...items, ...finalResult] : finalResult;
        dispatch(actions.setItems(newItems));
        dispatch(actions.setIsFullFilled(finalResult.length < first));
      })
      .finally(() => {
        dispatch(actions.setLoading(false));
      });
  };

  const handleItemClick = async (
    item: ItemSerialized,
    pluginType: MEDIA_TYPES_LABEL,
    itemToIframelyCallback?: (item: ItemSerialized) => Promise<IframelyMedia>,
  ) => {
    itemClickCallback && itemClickCallback(item);

    let newSelectedItems, newMedias;

    const isAlreadySelected =
      state.selectedItems.findIndex(({ id }) => id === item.id) > -1;

    // Add item/media
    if (!isAlreadySelected) {
      dispatch(actions.setBusy(true));

      const newItem = { ...item, pluginType };

      // We allows only one item/media when allowMultiSelect is set to true
      newSelectedItems = [
        ...(allowMultiSelect ? state.selectedItems : []),
        newItem,
      ];

      try {
        const newMedia = await (itemToIframelyCallback?.(item) ||
          itemToIframely(item, state.pluginType));

        if (!newMedia) {
          throw new Error();
        } else {
          newMedias = [...(allowMultiSelect ? state.medias : []), newMedia];
          dispatch(actions.setMedias(newMedias));
        }
      } catch (error) {}

      dispatch(actions.setSelectedItems(newSelectedItems));
      dispatch(actions.setBusy(false));
    }

    // Remove item/media
    else {
      const indexItemToRemove = state.selectedItems.findIndex(
        (selectedItem) => selectedItem.id === item.id,
      );
      newMedias = state.medias.filter(
        (_, index) => index !== indexItemToRemove,
      );
      newSelectedItems = state.selectedItems.filter(
        (_, index) => index !== indexItemToRemove,
      );

      dispatch(actions.setMedias(newMedias));
      dispatch(actions.setSelectedItems(newSelectedItems));
    }

    // We trigger custom onClick function to catching event
    // and operate consequently if needed
    onClick?.(
      item,
      newSelectedItems || state.selectedItems,
      newMedias || state.medias,
    );
  };

  const handleSearchMore = (opts?: IndexRange) => {
    return fetchItems(opts);
  };

  const handleOpen = (pluginType: string, open: boolean) => {
    dispatch(actions.setChangePlugin(pluginType));
    dispatch(actions.setIsOpen(open));
  };

  const handleSave = useCallback(() => {
    const { selectedItems, medias: selectedMedias } = state;

    setManagerLoading?.(true);

    try {
      if (allowMultiSelect) {
        addMedia?.(selectedMedias.map((media) => mediaFromIframely(media)));
      } else {
        updateMedia?.(mediaFromIframely(selectedMedias[0]), 0);
      }
    } catch (error) {
      console.error("Erreur lors de l'enregistrement ==> ", error);
    }

    setManagerLoading?.(false);
    dispatch(actions.setIsOpen(false));

    // We trigger custom onSave function to catching event
    // and operate consequently if needed
    onSave?.(selectedItems, selectedMedias);
  }, [
    addMedia,
    allowMultiSelect,
    onSave,
    setManagerLoading,
    state,
    updateMedia,
  ]);

  useEffect(() => {
    if (!state.isOpen) {
      dispatch(actions.setInitialState());
      return;
    }

    if (state.pluginType) {
      fetchItems();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.isOpen, state.pluginType, debouncedSearch]);

  useEffect(() => {
    // Input value from props
    if (defaultText) {
      setSearchParams((searchParamsPrevState) => ({
        ...searchParamsPrevState,
        search: defaultText,
      }));
    }

    // Open/Close modal state from props
    if (!defaultActions && allowedMedias.length) {
      handleOpen(initialTypeSelected || allowedMedias[0].type, true);
    }

    // View mode from props
    if (viewMode) {
      dispatch(actions.setViewMode(viewMode));
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewMode, defaultActions, defaultText]);

  useEffect(() => {
    const inferredPluginType =
      state.pluginType === ('' as MEDIA_TYPES_LABEL) &&
      allowedMedias[0].type === MEDIA_TYPES_LABEL.ARTICLES
        ? MEDIA_TYPES_LABEL.ARTICLES
        : state.pluginType;

    if (!allowedSuggestedMedias.includes(inferredPluginType)) {
      dispatch(actions.setSuggestedItems(null));
      return;
    }

    if (state.isOpen) {
      const articleFormat = getArticleFormat(inferredPluginType);
      suggestArticlesFn &&
        articleFormat &&
        suggestArticlesFn(articleFormat).then((res) => {
          dispatch(actions.setSuggestedItems(res));
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [suggestArticlesFn, allowedMedias, state.isOpen, state.pluginType]);

  return (
    <>
      <Dialog
        classes={{ paper: classes.dialogPaper }}
        maxWidth="xl"
        onClose={() => {
          dispatch(actions.setIsOpen(false));
          onClose?.();
        }}
        open={state.isOpen}
      >
        {/* Topbar */}
        {customToolbar ? (
          customToolbar
        ) : (
          <MultimediaToolbar
            handleSave={handleSave}
            isBusy={state.isBusy}
            onClose={onClose}
            selectedItems={state.selectedItems}
            setIsOpen={(open) => {
              dispatch(actions.setIsOpen(open));
            }}
          >
            {/* To show the list of selected medias */}
            <MultimediaList
              itemToRender={itemToRender}
              items={state.selectedItems}
              isDisabled={state.isBusy}
              isLoading={state.isLoading}
              onItemClick={handleItemClick}
              pluginType={state.pluginType}
              selectedItems={state.selectedItems}
              viewMode={ViewMode.GRID_HORIZONTAL}
            />
          </MultimediaToolbar>
        )}
        <div className={classes.innerWrapper}>
          {/* Sidebar */}
          <MultimediaTabs
            allowedMedias={allowedMedias}
            pluginType={state.pluginType}
            selectedItems={state.selectedItems}
            setChangePlugin={(plugin) => {
              dispatch(actions.setChangePlugin(plugin));
            }}
          />

          {/* Content */}
          <DialogContent className={classes.dialogContent}>
            {allowedMedias.map(
              ({
                Component: CustomMediaContent,
                props: customMediaContentProps,
                type,
              }) => {
                const isActive = state.pluginType === type;

                if (isActive) {
                  const defaultMediaContentProps = {
                    allowSearch,
                    isDisabled: state.isBusy,
                    isLoading: state.isLoading,
                    itemToRender,
                    suggestedItems:
                      Object.keys(searchParams).length > 1 ||
                      searchParams?.search?.length
                        ? null
                        : state.suggestedItems,
                    items: state.items,
                    key: type,
                    advancedSearch,
                    medias: state.medias,
                    onItemClick: handleItemClick,
                    pluginType: state.pluginType,
                    selectedItems: state.selectedItems,
                    value: state.value,
                    viewMode: state.viewMode,
                    fetchMoreMode: InfiniteListMode.HYBRIDE,
                    ...(!state.isFullFilled && {
                      fetchMore: handleSearchMore,
                    }),
                  };

                  return CustomMediaContent ? (
                    // Custom tab content component
                    <CustomMediaContent
                      {...defaultMediaContentProps}
                      {...customMediaContentProps}
                      setMedias={(medias: IframelyMedia[]) => {
                        dispatch(actions.setMedias(medias));
                      }}
                      setItems={(items: ItemSerialized[]) => {
                        dispatch(actions.setItems(items));
                      }}
                    />
                  ) : (
                    // Default tab content component
                    <DefaultMediaContent
                      {...defaultMediaContentProps}
                      multimediaSearchProps={{
                        fetchItems,
                        searchParams,
                        setSearchParams,
                        advancedSearch,
                        onViewModeChange: (newViewMode: ViewMode) => {
                          dispatch(actions.setViewMode(newViewMode));
                        },
                        placeholder,
                      }}
                    />
                  );
                }
              },
            )}
          </DialogContent>
        </div>
      </Dialog>

      {defaultActions && (
        <div style={customStyles}>
          {allowedMedias.map(({ badgeContent, label, Icon, type }) => (
            <Badge
              color="primary"
              badgeContent={badgeContent}
              key={`badge-${type}`}
            >
              <IconLabelButton
                key={type}
                label={label}
                data-testid={`multimedia-search-plugin-btn-${label}`}
                onClick={() => handleOpen(type, true)}
              >
                <Icon />
              </IconLabelButton>
            </Badge>
          ))}
        </div>
      )}
    </>
  );
};
